import { ReactElement, use } from 'react';
import { HandshakeOutlined } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { SimpleTreeView } from '@mui/x-tree-view';
import Constants from 'style/constants';
import OfficeBuildingMarker from 'shared/components/icons/office-building-marker';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import OrganisationUnitTreeItem from 'shared/features/organisation/components/organisation-tree-item/organisation-unit-tree-item';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { SubscribableOrganisationUnit } from 'features/care-connect-settings/domain/models/subscribable-organisation-unit-tree-response';
import useCareConnectOrganisationUnitTree from 'features/care-connect-settings/hooks/care-connect-organisation-unit-tree';
import styles from './care-connect-organisation-unit-tree.module.scss';

const CareConnectOrganisationUnitTree = (): ReactElement => {
  const { subscribableOrganisationUnitTreeIsLoading, subscribableOrganisationUnitTreeIsSuccess } = use(CareConnectSettingsProviderContext);

  const { organisationUnits, expandedNodes, selectedOrganisationUnitId, handleOnItemClick } = useCareConnectOrganisationUnitTree();

  const renderStartingIcons = (node: SubscribableOrganisationUnit): ReactElement => (
    <div className={styles.startingIcons}>{node.isLocation && <OfficeBuildingMarker sx={{ fontSize: 16, color: 'black' }} />}</div>
  );

  const renderEndingIcons = (node: SubscribableOrganisationUnit): ReactElement => {
    const callPlanNumbers = [node.outgoingExternalNumber, ...node.internalNumbers].filter((number): number is string => number !== null);

    return (
      <>
        {node.hasActiveCallPlan && (
          <HandshakeOutlined sx={{ width: 16, height: 16, color: Constants.Colors.primary, transform: 'scaleX(-1)' }} />
        )}
        {callPlanNumbers.length > 0 && <Typography variant="caption">{callPlanNumbers.join(' | ')}</Typography>}
      </>
    );
  };

  const renderChildNodes = (node: SubscribableOrganisationUnit): ReactElement[] =>
    node.childSubscriptionChannels.map((child) => (
      <OrganisationUnitTreeItem
        key={child.id}
        itemId={child.id}
        labelText={child.name}
        disableSelection={false}
        isRoot={false}
        showExpandIndicator={!child.isRoot}
        startingIcons={renderStartingIcons(child)}
        data-testid={`careConnectOrganisationUnitTreeItem-${child.id}`}
        endingIcons={renderEndingIcons(child)}
      >
        {renderChildNodes(child)}
      </OrganisationUnitTreeItem>
    ));

  if (subscribableOrganisationUnitTreeIsLoading) {
    return <LoadingIndicator />;
  }

  if (!subscribableOrganisationUnitTreeIsSuccess || !organisationUnits) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <SimpleTreeView
        aria-label="care-connect-organisation-unit-tree"
        expandedItems={expandedNodes}
        selectedItems={selectedOrganisationUnitId}
        onItemClick={handleOnItemClick}
      >
        {organisationUnits.map((rootItem) => (
          <OrganisationUnitTreeItem
            key={rootItem.id}
            itemId={rootItem.id}
            labelText={rootItem.name}
            disableSelection={false}
            isRoot={false}
            showExpandIndicator={!rootItem.isRoot}
            startingIcons={renderStartingIcons(rootItem)}
            data-testid={`careConnectOrganisationUnitTreeItem-${rootItem.id}`}
            endingIcons={renderEndingIcons(rootItem)}
          >
            {renderChildNodes(rootItem)}
          </OrganisationUnitTreeItem>
        ))}
      </SimpleTreeView>
    </div>
  );
};

export default CareConnectOrganisationUnitTree;
